import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
  Divider,
  Tab,
  Tabs,
  Container,
  Card,
  CardContent,
  Toolbar,
  AppBar,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CodeIcon from '@mui/icons-material/Code';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { tr } from 'date-fns/locale';
import enUS from 'date-fns/locale/en-US';
import { format, parseISO, isValid, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PaymentReceiptModal from './PaymentReceiptModal';
import TransactionPaymentMethodChangeModal from './TransactionPaymentMethodChangeModal';
import TransactionLogsModal from './TransactionLogsModal';
import GuideButton from '../../common/guide/GuideButton';
import InvoiceDisplayModal from '../invoice/InvoiceDisplayModal';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// İşlem türleri için sabit tanımlamalar
const TRANSACTION_TYPES = {
  SALE_WITH_STOCK: 'SALE_WITH_STOCK',
  SALE_WITHOUT_STOCK: 'SALE_WITHOUT_STOCK',
  COLLECTION: 'COLLECTION',
  PAYMENT: 'PAYMENT',
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
  INVOICE: 'invoice', // Fatura işlemi türü (eski)
  OFFSETTING: 'offsetting', // Mahsup işlemi
  QUICK_SALE: 'QUICK_SALE', // Hızlı Satış işlem türü
  SALES_INVOICE: 'SALES_INVOICE', // Satış Faturası (yeni SQL format)
  PURCHASE_INVOICE: 'PURCHASE_INVOICE', // Alış Faturası (yeni SQL format)
  // ✅ Çek/Senet işlem türleri
  CHECK_RECEIVED: 'CHECK_RECEIVED', // Alınan Çek
  NOTE_RECEIVED: 'NOTE_RECEIVED', // Alınan Senet
  CHECK_GIVEN: 'CHECK_GIVEN', // Verilen Çek
  NOTE_GIVEN: 'NOTE_GIVEN' // Verilen Senet
};

// İşlem türlerinin görünen isimleri
const TRANSACTION_TYPE_NAMES = {
  [TRANSACTION_TYPES.SALE_WITH_STOCK]: 'Stoklu Satış',
  [TRANSACTION_TYPES.SALE_WITHOUT_STOCK]: 'Stoksuz Satış',
  [TRANSACTION_TYPES.COLLECTION]: 'Tahsilat',
  [TRANSACTION_TYPES.PAYMENT]: 'Ödeme',
  [TRANSACTION_TYPES.CREDIT]: 'Alacaklandırma',
  [TRANSACTION_TYPES.DEBIT]: 'Borçlandırma',
  [TRANSACTION_TYPES.INVOICE]: 'Fatura',
  [TRANSACTION_TYPES.OFFSETTING]: 'Mahsup',
  [TRANSACTION_TYPES.QUICK_SALE]: 'Hızlı Satış',
  [TRANSACTION_TYPES.SALES_INVOICE]: 'Satış Faturası',
  [TRANSACTION_TYPES.PURCHASE_INVOICE]: 'Alış Faturası',
  // ✅ Çek/Senet işlem türleri
  [TRANSACTION_TYPES.CHECK_RECEIVED]: 'Alınan Çek',
  [TRANSACTION_TYPES.NOTE_RECEIVED]: 'Alınan Senet',
  [TRANSACTION_TYPES.CHECK_GIVEN]: 'Verilen Çek',
  [TRANSACTION_TYPES.NOTE_GIVEN]: 'Verilen Senet'
};

// ✅ Tahsilat kategorisine giren işlem türleri
// NOT: Çek/Senet alımları (CHECK_RECEIVED, NOTE_RECEIVED) tahsilat DEĞİL, portföye giriştir.
// Gerçek tahsilat, çek tahsil edildiğinde (COLLECTED) COLLECTION tipiyle kaydedilir.
const COLLECTION_TYPES = [
  TRANSACTION_TYPES.COLLECTION
];

// ✅ Ödeme kategorisine giren işlem türleri
// NOT: Çek/Senet verimleri (CHECK_GIVEN, NOTE_GIVEN) ödeme DEĞİL, portföyden çıkıştır.
// Gerçek ödeme, verilen çek/senet ödendiğinde (GIVEN_PAID) PAYMENT tipiyle kaydedilir.
const PAYMENT_TYPES = [
  TRANSACTION_TYPES.PAYMENT
];

// Ödeme yöntemlerinin görünen isimleri
const PAYMENT_METHOD_NAMES = {
  'cash': 'Nakit',
  'credit_card': 'Kredi Kartı',
  'card_payment': 'POS', // ✅ Backend'den gelen POS ödeme yöntemi
  'bank_transfer': 'Havale/EFT',
  'check': 'Çek',
  'promissory_note': 'Senet', // ✅ Senet ödeme yöntemi
  'note': 'Senet', // ✅ Alternatif senet key
  'pos': 'POS',
  'accrual': 'Tahakkuk' // ✅ Tahakkuk (kasa/banka hareketi olmayan cari kayıt)
} as any; // Tip hatası çözümü için any kullanıyoruz

// İşlem arayüzü
interface Transaction {
  id: string;
  accountId: string;
  accountName?: string;
  type: string;
  transactionType?: string; // Yeni işlem türü alanı
  operationType?: string; // İşlem operasyon türü (sales, purchase, vs.)
  amount: number;
  debitAmount?: number; // Borç miktarı
  creditAmount?: number; // Alacak miktarı
  debitBalance?: number; // İşlem sonrası borç bakiyesi
  description: string;
  documentNo?: string; // Belge No (fatura no olabilir)
  referenceId?: string; // Fatura veya belge ID'si
  referenceType?: string; // Referansın türü (salesinvoice, purchaseinvoice, vs)
  invoiceNumber?: string; // Fatura/çek belge numarası (snapshot)
  date?: string; // İşlem tarihi
  createdAt: string;
  createdBy?: string;
  isIncome: boolean; // Gelir mi gider mi?
  currency?: string; // Para birimi
  exchangeRate?: number; // Döviz kuru
  paymentMethod?: string; // Ödeme yöntemi
  previousBalance?: number; // Önceki bakiye
  nextBalance?: number; // Sonraki bakiye
  cashboxId?: string; // Kasa ID (nakit işlemler için)
  cashboxName?: string; // Kasa adı
  bankAccountId?: string; // Banka hesabı ID (banka işlemleri için)
  bankAccountName?: string; // Banka hesabı adı
}

interface AccountTransactionsProps {
  /** Route parametresi yerine prop ile hesap ID (manager paneli embedded modu) */
  accountId?: string;
  /** Manager paneli: şube filtresi bu şubeye kilitlenir */
  lockedBranchId?: string | null;
  /** Embedded modda geri butonu bu callback'i çağırır (route'a dönmek yerine) */
  onBack?: () => void;
}

const AccountTransactions: React.FC<AccountTransactionsProps> = ({
  accountId: accountIdProp,
  lockedBranchId = null,
  onBack
}) => {
  const { t, i18n } = useTranslation('accounting');
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const dateLocale = isTr ? tr : enUS;
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    if (!/[ÃÂÅÄ]/.test(value)) return value;
    try {
      return decodeURIComponent(escape(value));
    } catch {
      return value;
    }
  };
  const navigate = useNavigate();
  const { accountId: routeAccountId } = useParams<{ accountId: string }>();
  const accountId = accountIdProp || routeAccountId;
  const { userDetails, branches } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // ✅ Backend'den hesaplanmış bakiyeler (son 1 ay veya filtrelenmiş aralık)
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [debitBalance, setDebitBalance] = useState(0); // Filtreleri göster/gizle
  
  // Tarih filtresi için state'ler
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // İşlem türü filtresi için state
  const [selectedTransactionTypeFilter, setSelectedTransactionTypeFilter] = useState<string>('');

  // Şube filtresi için state (eski kayıtlarda branchId yok = Genel; manager panelinde kilitli)
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>(lockedBranchId || '');

  // Excel indirme için state
  const [loadingExport, setLoadingExport] = useState(false);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalTransactionsCount, setTotalTransactionsCount] = useState(0); // Backend'den gelen toplam kayıt sayısı
  const [isSqlAccount, setIsSqlAccount] = useState(false); // SQL mi Firebase mi?

  // Detay modalı için state'ler
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTransactionForDetail, setSelectedTransactionForDetail] = useState<Transaction | null>(null);

  // İşlem menüsü için state'ler
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Ödeme yöntemi değiştirme modalı için state
  const [isPaymentMethodChangeModalOpen, setIsPaymentMethodChangeModalOpen] = useState(false);

  // Silme onay modalı için state'ler
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);

  // Başarı snackbar için state'ler
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Hata snackbar için state'ler
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Loglar modalı için state
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  // Fatura görüntüleme modalı için state'ler
  const [isInvoiceDisplayModalOpen, setIsInvoiceDisplayModalOpen] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<any>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // İşlem ayrıntıları modalı için state
  const [isTransactionDetailsModalOpen, setIsTransactionDetailsModalOpen] = useState(false);
  const [transactionForDetails, setTransactionForDetails] = useState<Transaction | null>(null);

  const getTransactionTypeName = (type?: string, operationType?: string) => {
    if (!type) return t('current.report.transactionTypes.other');
    if (type === TRANSACTION_TYPES.INVOICE && operationType === 'sales') return t('current.report.transactionTypes.salesInvoice');
    if (type === TRANSACTION_TYPES.INVOICE && operationType === 'purchase') return t('current.report.transactionTypes.purchaseInvoice');

    const map: Record<string, string> = {
      [TRANSACTION_TYPES.SALE_WITH_STOCK]: t('current.report.transactionTypes.saleWithStock'),
      [TRANSACTION_TYPES.SALE_WITHOUT_STOCK]: t('current.report.transactionTypes.saleWithoutStock'),
      [TRANSACTION_TYPES.COLLECTION]: t('current.report.transactionTypes.collection'),
      [TRANSACTION_TYPES.PAYMENT]: t('current.report.transactionTypes.payment'),
      [TRANSACTION_TYPES.CREDIT]: t('current.report.transactionTypes.credit'),
      [TRANSACTION_TYPES.DEBIT]: t('current.report.transactionTypes.debit'),
      [TRANSACTION_TYPES.INVOICE]: t('current.report.transactionTypes.invoice'),
      [TRANSACTION_TYPES.OFFSETTING]: t('current.report.transactionTypes.offsetting'),
      [TRANSACTION_TYPES.QUICK_SALE]: t('current.report.transactionTypes.quickSale'),
      [TRANSACTION_TYPES.SALES_INVOICE]: t('current.report.transactionTypes.salesInvoice'),
      [TRANSACTION_TYPES.PURCHASE_INVOICE]: t('current.report.transactionTypes.purchaseInvoice'),
      [TRANSACTION_TYPES.CHECK_RECEIVED]: t('current.report.transactionTypes.checkReceived'),
      [TRANSACTION_TYPES.NOTE_RECEIVED]: t('current.report.transactionTypes.noteReceived'),
      [TRANSACTION_TYPES.CHECK_GIVEN]: t('current.report.transactionTypes.checkGiven'),
      [TRANSACTION_TYPES.NOTE_GIVEN]: t('current.report.transactionTypes.noteGiven')
    };
    return map[type] || t('current.report.transactionTypes.other');
  };

  const getPaymentMethodName = (method?: string) => {
    const map: Record<string, string> = {
      cash: tt('Nakit', 'Cash'),
      credit_card: tt('Kredi Kartı', 'Credit Card'),
      card_payment: 'POS',
      bank_transfer: tt('Havale/EFT', 'Bank Transfer/EFT'),
      check: tt('Çek', 'Check'),
      promissory_note: tt('Senet', 'Promissory Note'),
      note: tt('Senet', 'Promissory Note'),
      pos: 'POS',
      accrual: tt('Tahakkuk', 'Accrual')
    };
    return method ? map[method] || method : '-';
  };

  const handleGoBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  const handleOpenLogsModal = () => {
    setIsLogsModalOpen(true);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
  };

  // Tarih filtresini temizle
  const handleClearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedTransactionTypeFilter(''); // İşlem türü filtresini de temizle
    setSelectedBranchFilter(lockedBranchId || ''); // Şube filtresini de temizle (kilitliyse kilitli şube kalır)
    setPage(0); // Filtre temizlendiğinde ilk sayfaya dön
  };

  // Şirket ID'sini al
  useEffect(() => {
    if (userDetails?.companyId) {
      setCompanyId(userDetails.companyId);
    } else {
      setError("Şirket bilgisi bulunamadı.");
      setLoading(false);
    }
  }, [userDetails]);

  // Cari hesap detaylarını yükleme (isim ve bakiyeler için)
  useEffect(() => {
    if (!companyId || !accountId) return;
    
    const loadAccountDetails = async () => {
      try {
        console.log("Cari hesap detayları yükleniyor...", { companyId, accountId });
        
        const numericId = parseInt(accountId, 10);
        const isNumericId = !isNaN(numericId) && numericId.toString() === accountId;
        
        if (isNumericId) {
          if (!companyId) {
            throw new Error('CompanyId bulunamadı');
          }
          
          const detailApiUrl = ACCOUNTING_API.ACCOUNT_BY_ID(numericId);
          console.log("📡 [ACCOUNT-DETAIL] API çağrısı:", detailApiUrl);
          
          const response = await accountingApi.get(detailApiUrl);
          
          const result = response.data;
          const accountData = result.data || result;
          
          if (accountData) {
            console.log('📊 [ACCOUNT-DETAIL] API RAW accountData:', JSON.stringify({
              id: accountData.id, companyName: accountData.companyName,
              creditBalance: accountData.creditBalance, debitBalance: accountData.debitBalance,
              balance: accountData.balance, isActive: accountData.isActive
            }, null, 2));
            console.log(`📊 [ACCOUNT-DETAIL] Net bakiye: creditBalance(${accountData.creditBalance}) - debitBalance(${accountData.debitBalance}) = ${(accountData.creditBalance || 0) - (accountData.debitBalance || 0)}`);
            setDebitBalance(accountData.debitBalance || 0);
            setTotalCredit(accountData.creditBalance || 0);
            setTotalDebit(accountData.debitBalance || 0);
            setAccountName(decodeMojibake(accountData.companyName || tt('Cari Hesap', 'Current Account')));
            console.log("✅ Cari hesap detayları SQL'den yüklendi:", accountData);
          } else {
            console.log("❌ Cari hesap verisi bulunamadı (SQL)");
            setError("Cari hesap bulunamadı.");
          }
        } else {
          // Legacy firebase-id hesap: tum cariler SQL'e tasindigi icin CF/RTDB okumasi kaldirildi.
          // Boyle bir id ile bu ekrana gelinmemesi gerekir; guvenli hata durumu.
          console.warn("⚠️ Sayisal olmayan (legacy firebase) hesap id'si; SQL'de karsiligi yok:", accountId);
          setError("Cari hesap bulunamadı.");
        }
      } catch (error) {
        console.error('❌ Cari hesap detayları yüklenirken hata:', error);
        setError(handleApiError(error));
      }
    };
    
    loadAccountDetails();
  }, [companyId, accountId]);

  // Hesap bazında bakiyeleri hesapla
  const calculateBalances = (transactionsList: Transaction[]) => {
    // NOT: Artık bakiye hesaplaması yapmıyoruz, direkt veritabanından gelen değerleri kullanıyoruz
    // Bu fonksiyon sadece referans için burada bırakılmıştır
    
    console.log("Bakiye hesaplaması yapılmadı: Doğrudan veritabanından gelen değerler kullanılıyor");
  };

  // İşlemleri yükleme (SQL ve Firebase desteği)
  useEffect(() => {
    if (!companyId || !accountId) return;
    
    setLoading(true);
    setError(null);
    
    // Account ID'nin sayısal olup olmadığını kontrol et
    const numericId = parseInt(accountId, 10);
    const isNumericId = !isNaN(numericId) && numericId.toString() === accountId;
    
    if (isNumericId) {
      // SQL ID - SQL API'den sayfalama ile oku
      setIsSqlAccount(true);
      console.log("✅ SQL ID tespit edildi, işlemler SQL API'den SAYFALI yükleniyor:", numericId);
      
      const loadTransactionsFromSQL = async () => {
        try {
          const backendPage = page + 1;
          const apiUrl = ACCOUNTING_API.TRANSACTIONS_BY_ACCOUNT_PAGED(numericId, backendPage, rowsPerPage, {
            sortColumn: 'date',
            sortDirection: 'desc',
            startDate: startDate ? startDate.toISOString() : undefined,
            endDate: endDate ? endDate.toISOString() : undefined,
            transactionType: selectedTransactionTypeFilter || undefined,
            branchId: selectedBranchFilter || undefined,
          });
          console.log("📡 API URL:", apiUrl);
          
          const response = await accountingApi.get(apiUrl);
          
          const result = response.data;
          
          // Sayfalama bilgilerini al
          const sqlTransactions = result.data || [];
          const paginationInfo = result.pagination || {};
          const totalCount = paginationInfo.totalCount || sqlTransactions.length;
          
          console.log("📊 Sayfalama bilgisi:", { 
            page: paginationInfo.page, 
            pageSize: paginationInfo.pageSize, 
            totalCount, 
            totalPages: paginationInfo.totalPages 
          });
          
          // Toplam kayıt sayısını güncelle
          setTotalTransactionsCount(totalCount);
          
          // SQL verilerini frontend formatına çevir
          const formattedTransactions: Transaction[] = sqlTransactions.map((data: any) => {
            const isCollection = data.transactionType === TRANSACTION_TYPES.COLLECTION;
            
            return {
              id: data.id.toString(),
              accountId: data.accountId ? data.accountId.toString() : accountId,
              accountName: decodeMojibake(data.accountName || accountName),
              transactionType: data.transactionType || '',
              operationType: data.operationType || '',
              type: data.transactionType || 'DEBIT',
              amount: parseFloat(data.amount) || 0,
              debitAmount: parseFloat(data.debitAmount) || 0,
              creditAmount: parseFloat(data.creditAmount) || 0,
              debitBalance: parseFloat(data.debitBalance) || 0,
              description: decodeMojibake(data.description || ''),
              documentNo: data.referenceId || data.documentNo || '',
              referenceId: data.referenceId || '',
              referenceType: data.referenceType || '',
              invoiceNumber: data.invoiceNumber || '',
              date: data.date || data.createdAt || '',
              createdAt: data.createdAt || '',
              currency: data.currency || 'TRY',
              exchangeRate: parseFloat(data.exchangeRate) || 1,
              isIncome: isCollection ? false : (data.creditAmount > 0),
              paymentMethod: data.paymentMethod || '',
              previousBalance: data.previousBalance !== undefined ? parseFloat(data.previousBalance) : undefined,
              nextBalance: data.nextBalance !== undefined ? parseFloat(data.nextBalance) : undefined
            };
          });
          
          // ✅ Backend zaten sıralı gönderiyor, tekrar sıralamaya gerek yok
          console.log("✅ İşlemler SQL'den SAYFALI yüklendi:", formattedTransactions.length, "adet (Toplam:", totalCount, ")");
          setTransactions(formattedTransactions);
          setLoading(false);
        } catch (error) {
          console.error('❌ SQL işlemler yüklenirken hata:', error);
          setError(handleApiError(error));
          setLoading(false);
        }
      };
      
      loadTransactionsFromSQL();
      
      // SQL için cleanup gerek yok (real-time değil)
      return () => {};
    } else {
      // Legacy firebase-id hesap: tum cariler SQL'e tasindigi icin RTDB okumasi kaldirildi.
      // Boyle bir id ile bu ekrana gelinmemesi gerekir; guvenli bos durum.
      setIsSqlAccount(false);
      console.warn("⚠️ Sayisal olmayan (legacy firebase) hesap id'si; SQL'de karsiligi yok:", accountId);
      setTransactions([]);
      setLoading(false);
    }
  }, [companyId, accountId, page, rowsPerPage, startDate, endDate, selectedTransactionTypeFilter, selectedBranchFilter]);

  // Tarih formatla (Yerel saat - Backend'den zaten Türkiye saatinde geliyor, saniyesiz)
  const formatDate = (dateString: string, transaction?: any) => {
    if (!dateString) return '-';
    
    try {
      // Date nesnesine çevir
      let date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        // Geçersiz tarih formatı
        return dateString;
      }
      
      // ✅ YENİ: Backend'den zaten yerel saat (Türkiye) geliyor, +3 eklemeye gerek yok
      // Manuel formatla (yerel saat, saniyesiz)
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return dateString; // Hata durumunda orijinal dizeyi döndür
    }
  };

  // Para birimi formatla
  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Tab değişimi
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(tt('Cari Hareketler', 'Current Transactions'));
      
      // Başlık satırı - Cari hesap bilgileri
      worksheet.mergeCells('A1:H1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `${tt('Cari Hesap', 'Current Account')}: ${accountName}`;
      titleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Cari hesap özet bilgileri
      worksheet.addRow([tt('Toplam Alacak', 'Total Credit'), formatCurrency(totalCredit), '', '', tt('Toplam Borç', 'Total Debit'), formatCurrency(debitBalance), '', '']);
      worksheet.addRow([tt('Bakiye', 'Balance'), formatCurrency(totalCredit - debitBalance), '', '', '', '', '', '']);
      
      // Stil tanımlamaları
      const summaryStyle = {
        font: { bold: true },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFEAEAEA' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Özet bilgilerin stillerini uygula
      worksheet.getRow(2).eachCell((cell, colNumber) => {
        if (colNumber <= 2 || (colNumber >= 5 && colNumber <= 6)) {
          cell.style = summaryStyle;
        }
      });
      
      worksheet.getRow(3).eachCell((cell, colNumber) => {
        if (colNumber <= 2) {
          cell.style = summaryStyle;
        }
      });
      
      // Boş satır ekle
      worksheet.addRow([]);
      
      // Sütunları tanımla
      worksheet.addRow([
        tt('Tarih', 'Date'),
        tt('İşlem Türü', 'Transaction Type'),
        tt('Ödeme Yöntemi', 'Payment Method'),
        tt('Açıklama', 'Description'),
        tt('Borç', 'Debit'),
        tt('Alacak', 'Credit'),
        tt('Bakiye', 'Balance'),
        tt('Para Birimi', 'Currency')
      ]);
      
      // Tablo başlık stili
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      const headerRowIndex = 5; // Yeni başlık satırı dizini (1: başlık, 2-3: özet, 4: boş satır, 5: tablo başlığı)
      worksheet.getRow(headerRowIndex).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Sütun genişliklerini ayarla
      worksheet.columns = [
        { width: 20 }, // Tarih
        { width: 20 }, // İşlem Türü
        { width: 20 }, // Ödeme Yöntemi
        { width: 40 }, // Açıklama
        { width: 15 }, // Önceki Bakiye
        { width: 15 }, // Tutar
        { width: 15 }, // Bakiye
        { width: 10 }, // Para Birimi
      ];
      
      // Filtrelenmiş işlemleri Excel'e aktar (filtrelenmiş listeyi kullan)
      dateAndTypeFilteredTransactions.forEach(transaction => {
        const type = transaction.transactionType || transaction.type;
        let typeName = getTransactionTypeName(type, transaction.operationType);
        
        // Fatura işlemi türleri
        if (type === TRANSACTION_TYPES.INVOICE) {
          if (transaction.operationType === 'sales') {
            typeName = 'Satış Faturası';
          } else if (transaction.operationType === 'purchase') {
            typeName = 'Alış Faturası';
          }
        }
        
        worksheet.addRow([
          transaction.date 
            ? formatDate(transaction.date, transaction) 
            : formatDate(transaction.createdAt, transaction),
          typeName,
          getPaymentMethodName(transaction.paymentMethod),
          transaction.description || '-',
          transaction.debitAmount || 0,
          transaction.creditAmount || 0,
          transaction.nextBalance || 0,
          transaction.currency || 'TRY'
        ]);
      });
      
      // Para birimi formatlaması
      const formatRow = (row: any) => {
        const amountCols = [5, 6, 7]; // E, F, G sütunları
        amountCols.forEach(col => {
          const cell = row.getCell(col);
          if (typeof cell.value === 'number') {
            cell.numFmt = '#,##0.00 ₺';
          }
        });
      };
      
      // Tablodaki tüm satırlar için formatı uygula (başlık satırından sonraki satırlar)
      for (let i = headerRowIndex + 1; i <= worksheet.rowCount; i++) {
        formatRow(worksheet.getRow(i));
      }
      
      // Zebrastripe (alternatif satır renklendirme)
      for (let i = headerRowIndex + 1; i <= worksheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        worksheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `Cari_Hareketler_${accountName.replace(/\s+/g, '_')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log(`${dateAndTypeFilteredTransactions.length} adet cari hareket Excel dosyasına aktarıldı.`);
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError('Excel dışa aktarma sırasında bir hata oluştu.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Filtrelenmiş işlemler
  // SQL: Filtreler backend'e gönderildiği için frontend'de tekrar filtrelemeye GEREK YOK
  // Firebase: Frontend'de filtreleme yapılıyor
  const filteredTransactions = isSqlAccount
    ? transactions
    : activeTab === 0 
      ? transactions
      : activeTab === 1 
        ? transactions.filter(t => t.isIncome && !COLLECTION_TYPES.includes(t.transactionType || ''))
        : activeTab === 2
          ? transactions.filter(t => !t.isIncome && !PAYMENT_TYPES.includes(t.transactionType || ''))
          : transactions.filter(t => COLLECTION_TYPES.includes(t.transactionType || ''));

  const dateFilteredTransactions = isSqlAccount
    ? filteredTransactions
    : filteredTransactions.filter(transaction => {
        if (!startDate && !endDate) return true;
        
        const transactionDate = transaction.date 
          ? new Date(transaction.date) 
          : transaction.createdAt 
            ? new Date(transaction.createdAt) 
            : null;
        
        if (!transactionDate) return true;
        
        if (startDate && !endDate) {
          return isAfter(transactionDate, startOfDay(startDate)) || 
                 transactionDate.getTime() === startOfDay(startDate).getTime();
        }
        
        if (!startDate && endDate) {
          return isBefore(transactionDate, endOfDay(endDate)) || 
                 transactionDate.getTime() === endOfDay(endDate).getTime();
        }
        
        return (isAfter(transactionDate, startOfDay(startDate!)) || 
                transactionDate.getTime() === startOfDay(startDate!).getTime()) && 
               (isBefore(transactionDate, endOfDay(endDate!)) || 
                transactionDate.getTime() === endOfDay(endDate!).getTime());
      });

  const dateAndTypeFilteredTransactions = isSqlAccount
    ? dateFilteredTransactions
    : dateFilteredTransactions.filter(transaction => {
        if (!selectedTransactionTypeFilter) return true;
        const transactionType = transaction.transactionType || transaction.type;
        return transactionType === selectedTransactionTypeFilter;
      });

  const paginatedTransactions = isSqlAccount 
    ? dateAndTypeFilteredTransactions
    : dateAndTypeFilteredTransactions.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );

  // İşlemleri ve tarihleri loglamak için bir useEffect ekleyelim
  useEffect(() => {
    if (paginatedTransactions.length > 0) {
      console.log('Görüntülenen işlemler ve tarih bilgileri:');
      paginatedTransactions.forEach(transaction => {
        console.log(`İşlem ID: ${transaction.id}`);
        console.log(`Date: ${transaction.date}, CreatedAt: ${transaction.createdAt}`);
        console.log(`Formatlanmış tarih: ${
          transaction.date 
            ? formatDate(transaction.date, transaction) 
            : formatDate(transaction.createdAt, transaction)
        }`);
        console.log('------------------------');
      });
    }
  }, [paginatedTransactions]);

  // AccountingDataTable kolon tanımları
  const transactionColumns: DataTableColumn<Transaction>[] = [
    {
      id: 'date',
      label: tt('Tarih', 'Date'),
      sortable: true,
      render: (row) => (
        <>{row.date ? formatDate(row.date, row) : formatDate(row.createdAt, row)}</>
      ),
    },
    {
      id: 'transactionType',
      label: tt('İşlem Türü', 'Transaction Type'),
      sortable: true,
      render: (row) => renderTransactionType(row),
    },
    {
      id: 'paymentMethod',
      label: tt('Ödeme Yöntemi', 'Payment Method'),
      field: 'paymentMethod',
      sortable: true,
      render: (row) => (
        <>{getPaymentMethodName(row.paymentMethod)}</>
      ),
    },
    {
      id: 'description',
      label: tt('Açıklama', 'Description'),
      field: 'description',
      sortable: true,
      render: (row) => <>{row.description || ''}</>,
    },
    {
      id: 'debitAmount',
      label: tt('Borç', 'Debit'),
      field: 'debitAmount',
      align: 'right',
      sortable: true,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 'medium' }}>
          {row.debitAmount ? formatCurrency(row.debitAmount, row.currency || 'TRY') : '-'}
        </Box>
      ),
    },
    {
      id: 'creditAmount',
      label: tt('Alacak', 'Credit'),
      field: 'creditAmount',
      align: 'right',
      sortable: true,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 'medium' }}>
          {row.creditAmount ? formatCurrency(row.creditAmount, row.currency || 'TRY') : '-'}
        </Box>
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      align: 'center' as const,
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMenu(e, row);
          }}
          sx={{ color: 'text.secondary' }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Satır koşullu arka plan renkleri
  const getTransactionRowSx = (row: Transaction) => ({
    backgroundColor: row.transactionType === TRANSACTION_TYPES.COLLECTION
      ? 'rgba(76, 175, 80, 0.08)'
      : row.isIncome
        ? 'rgba(76, 175, 80, 0.04)'
        : 'rgba(244, 67, 54, 0.04)',
  });

  // İşlem tipine göre ikon ve renk belirle
  const getTransactionTypeIcon = (transaction: Transaction) => {
    // transactionType alanı varsa onu kullan
    const type = transaction.transactionType || transaction.type;
    const isIncome = transaction.isIncome;
    
    switch (type) {
      case TRANSACTION_TYPES.INVOICE:
        if (transaction.operationType === 'sales') {
          return <ReceiptIcon fontSize="small" color="primary" />;
        }
        return <ReceiptIcon fontSize="small" color="secondary" />;
      case TRANSACTION_TYPES.SALES_INVOICE:
        return <ShoppingBagIcon fontSize="small" color="primary" />;
      case TRANSACTION_TYPES.PURCHASE_INVOICE:
        return <ShoppingCartIcon fontSize="small" color="secondary" />;
      case TRANSACTION_TYPES.SALE_WITH_STOCK:
      case TRANSACTION_TYPES.SALE_WITHOUT_STOCK:
        return <ReceiptIcon fontSize="small" color="primary" />;
      case TRANSACTION_TYPES.COLLECTION:
        return <ArrowDownwardIcon fontSize="small" color="success" />;
      case TRANSACTION_TYPES.PAYMENT:
        return <ArrowUpwardIcon fontSize="small" color="error" />;
      case TRANSACTION_TYPES.CREDIT:
        return <ArrowDownwardIcon fontSize="small" color="success" />;
      case TRANSACTION_TYPES.DEBIT:
        return <ArrowUpwardIcon fontSize="small" color="error" />;
      case TRANSACTION_TYPES.QUICK_SALE:
        return <PointOfSaleIcon fontSize="small" color="info" />;
      default:
        return isIncome 
          ? <ArrowDownwardIcon fontSize="small" color="success" /> 
          : <ArrowUpwardIcon fontSize="small" color="error" />;
    }
  };

  // İşlemin türünü görüntüle
  const renderTransactionType = (transaction: Transaction) => {
    // transactionType alanı varsa onu kullan
    const type = transaction.transactionType || transaction.type;
    
    // Özel işlem türü isimleri
    let typeName = getTransactionTypeName(type, transaction.operationType);
    
    // Fatura ise ve operationType 'sales' veya 'purchase' ise, özel isim
    if (type === TRANSACTION_TYPES.INVOICE) {
      if (transaction.operationType === 'sales') {
        typeName = 'Satış Faturası';
      } else if (transaction.operationType === 'purchase') {
        typeName = 'Alış Faturası';
      }
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getTransactionTypeIcon(transaction)}
        <Typography variant="body2">
          {typeName}
        </Typography>
      </Box>
    );
  };

  // Para birimini görüntüleme
  const renderAmount = (transaction: Transaction) => {
    const currencyCode = transaction.currency || 'TRY';
    
    return formatCurrency(transaction.amount, currencyCode);
  };

  const handleOpenDetailModal = (transaction: Transaction) => {
    setSelectedTransactionForDetail(transaction);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setSelectedTransactionForDetail(null);
    setIsDetailModalOpen(false);
  };

  // Menü işlemleri
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => {
    event.stopPropagation(); // Satır tıklama olayını engelle
    
    // Transaction içeriğini kontrol et
    console.log("Seçilen transaction:", transaction);
    
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    // SelectedTransaction'ı modalı açtıktan sonra temizleyeceğiz
  };
  
  const handleChangePaymentMethod = () => {
    if (selectedTransaction) {
      // İşlem değiştirme modalını aç
      console.log("İşlem hareketini değiştir - selectedTransaction:", selectedTransaction);
      setIsPaymentMethodChangeModalOpen(true);
      handleCloseMenu();
    }
  };

  const handleDeleteTransaction = () => {
    if (!selectedTransaction) return;
    
    // Modalı aç ve işlemi sakla
    setTransactionToDelete(selectedTransaction);
    setIsDeleteConfirmModalOpen(true);
    handleCloseMenu();
  };

  // ✅ YENİ: Faturayı görüntüle (Modal aç)
  const handleViewInvoice = async () => {
    if (!selectedTransaction?.referenceId) {
      setErrorMessage('Fatura bilgisi bulunamadı.');
      setErrorSnackbarOpen(true);
      handleCloseMenu();
      return;
    }

    try {
      setLoadingInvoice(true);
      handleCloseMenu();

      console.log('📄 [FATURA GÖRÜNTÜLE] Fatura yükleniyor...', { 
        referenceId: selectedTransaction.referenceId,
        companyId: companyId
      });

      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error('CompanyId bulunamadı');
      }

      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(selectedTransaction.referenceId));

      const result = response.data;
      const invoiceData = result.data || result;

      console.log('✅ [FATURA GÖRÜNTÜLE] Fatura yüklendi:', invoiceData);

      // InvoiceDisplayModal için veri formatını düzenle
      const formattedInvoiceData = {
        id: invoiceData.id,
        invoiceNumber: invoiceData.invoiceNumber,
        type: invoiceData.invoiceType?.toLowerCase() === 'sales' ? 'sales' : 
              invoiceData.invoiceType?.toLowerCase() === 'purchase' ? 'purchase' : 'quick_sale',
        date: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        account: {
          companyName: invoiceData.accountName || accountName || '-',
          taxNumber: invoiceData.accountTaxNumber || '',
          taxOffice: invoiceData.accountTaxOffice || '',
          address: invoiceData.accountAddress || '',
        },
        items: invoiceData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.itemId,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit || 'adet',
          unitPrice: item.unitPrice,
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          lineTotal: item.lineTotal,
          netTotal: item.netTotal,
          vatAmount: item.vatAmount,
          sctAmount: item.sctAmount || 0,
          grossTotal: item.grossTotal,
        })) || [],
        subtotal: invoiceData.subtotal,
        vatTotal: invoiceData.vatTotal,
        sctTotal: invoiceData.sctTotal || 0,
        discountTotal: invoiceData.discountTotal || 0,
        grandTotal: invoiceData.grandTotal,
        originalInvoiceTotal: invoiceData.originalInvoiceTotal || null, // ✅ YENİ: Yuvarlama farkı için
        currency: invoiceData.currency || 'TRY',
        notes: invoiceData.notes || '',
        description: invoiceData.description || '',
      };

      setSelectedInvoiceData(formattedInvoiceData);
      setIsInvoiceDisplayModalOpen(true);
      setLoadingInvoice(false);

    } catch (error: any) {
      console.error('❌ [FATURA GÖRÜNTÜLE] Hata:', error);
      setErrorMessage(`Fatura yüklenemedi: ${handleApiError(error)}`);
      setErrorSnackbarOpen(true);
      setLoadingInvoice(false);
    }
  };

  // ✅ YENİ: Faturayı düzenle (Sayfaya yönlendir)
  const handleEditInvoice = () => {
    if (!selectedTransaction?.referenceId) {
      setErrorMessage('Fatura bilgisi bulunamadı.');
      setErrorSnackbarOpen(true);
      handleCloseMenu();
      return;
    }

    handleCloseMenu();

    const transactionType = selectedTransaction.transactionType;
    const invoiceId = selectedTransaction.referenceId;

    console.log('✏️ [FATURA DÜZENLE] Yönlendiriliyor...', { 
      transactionType, 
      invoiceId
    });

    // İşlem türüne göre doğru sayfaya yönlendir
    if (transactionType === 'SALES_INVOICE') {
      navigate(`/fatura/satis/duzenle/${invoiceId}`);
    } else if (transactionType === 'PURCHASE_INVOICE') {
      navigate(`/fatura/alis/duzenle/${invoiceId}`);
    } else {
      // Diğer durumlar için satış faturası sayfasına yönlendir
      navigate(`/fatura/satis/duzenle/${invoiceId}`);
    }
  };

  // Fatura görüntüleme modalını kapat
  const handleCloseInvoiceDisplayModal = () => {
    setIsInvoiceDisplayModalOpen(false);
    setSelectedInvoiceData(null);
  };

  // İşlem ayrıntıları modalını aç
  const handleOpenTransactionDetails = () => {
    if (!selectedTransaction) return;
    
    console.log('📋 [İŞLEM AYRINTILARI] İşlem detayları:', selectedTransaction);
    setTransactionForDetails(selectedTransaction);
    setIsTransactionDetailsModalOpen(true);
    handleCloseMenu();
  };

  // İşlem ayrıntıları modalını kapat
  const handleCloseTransactionDetails = () => {
    setIsTransactionDetailsModalOpen(false);
    setTransactionForDetails(null);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;

    try {
      setIsDeleteConfirmModalOpen(false);
      setLoading(true);
      setError(null);

      // Account ID'yi sayıya çevir (SQL ID)
      const numericTransactionId = parseInt(transactionToDelete.id, 10);
      if (isNaN(numericTransactionId)) {
        throw new Error('Geçersiz işlem ID formatı');
      }

      console.log('🗑️ İşlem siliniyor:', {
        id: numericTransactionId,
        type: transactionToDelete.transactionType,
        amount: transactionToDelete.amount
      });

      // Silme sebebini ve sileni belirle
      const deleteRequest = {
        companyId: companyId || '', // ✅ MULTI-TENANT: companyId eklendi
        reason: 'Kullanıcı tarafından manuel olarak silindi',
        deletedBy: userDetails 
          ? `${userDetails.firstName} ${userDetails.lastName}`.trim() || userDetails.email 
          : 'Unknown'
      };

      // SQL API'ye silme isteği gönder
      const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_DELETE(numericTransactionId), deleteRequest);

      const result = response.data;
      console.log('✅ İşlem silindi:', result);

      // Başarılı mesajı göster
      setSuccessMessage(`İşlem başarıyla silindi! Log ID: ${result.logId || 'N/A'}`);
      setSuccessSnackbarOpen(true);

      // İşlemleri yeniden yükle
      if (companyId && accountId) {
        const numericAccountId = parseInt(accountId, 10);
        const isNumericAccountId = !isNaN(numericAccountId) && numericAccountId.toString() === accountId;

        if (isNumericAccountId) {
          // SQL'den yeniden yükle
          const transactionsResponse = await accountingApi.get(ACCOUNTING_API.TRANSACTIONS_BY_ACCOUNT(numericAccountId));

          {
            const transactionsResult = transactionsResponse.data;
            const sqlTransactions = transactionsResult.data || transactionsResult;

            const formattedTransactions: Transaction[] = sqlTransactions.map((data: any) => {
              const isCollection = data.transactionType === TRANSACTION_TYPES.COLLECTION;

              return {
                id: data.id.toString(),
                accountId: data.accountId ? data.accountId.toString() : accountId,
                accountName: decodeMojibake(data.accountName || accountName),
                transactionType: data.transactionType || '',
                operationType: data.operationType || '',
                type: data.transactionType || 'DEBIT',
                amount: parseFloat(data.amount) || 0,
                debitAmount: parseFloat(data.debitAmount) || 0,
                creditAmount: parseFloat(data.creditAmount) || 0,
                debitBalance: parseFloat(data.debitBalance) || 0,
                description: decodeMojibake(data.description || ''),
                documentNo: data.referenceId || data.documentNo || '',
                referenceId: data.referenceId || '',
                referenceType: data.referenceType || '',
                invoiceNumber: data.invoiceNumber || '',
                date: data.date || data.createdAt || '',
                createdAt: data.createdAt || '',
                currency: data.currency || 'TRY',
                exchangeRate: parseFloat(data.exchangeRate) || 1,
                isIncome: isCollection ? false : (data.creditAmount > 0),
                paymentMethod: data.paymentMethod || '',
                previousBalance: data.previousBalance !== undefined ? parseFloat(data.previousBalance) : undefined,
                nextBalance: data.nextBalance !== undefined ? parseFloat(data.nextBalance) : undefined
              };
            });

            // İşlemleri tarihe göre sırala (en yeni en üstte)
            // ✅ Öncelik: date alanı varsa ona göre, yoksa createdAt'e göre sırala
            formattedTransactions.sort((a, b) => {
              const dateA = a.date ? new Date(a.date) : new Date(a.createdAt);
              const dateB = b.date ? new Date(b.date) : new Date(b.createdAt);
              return dateB.getTime() - dateA.getTime();
            });

            setTransactions(formattedTransactions);
            // ✅ Bakiyeler artık işlem kayıtlarından otomatik hesaplanacak (useMemo ile)
          }
        }
      }

      setLoading(false);
      setTransactionToDelete(null);
    } catch (error: any) {
      console.error('❌ İşlem silinirken hata:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      setError(handleApiError(error));
      setLoading(false);
      setTransactionToDelete(null);
      
      setErrorMessage(`İşlem silinemedi: ${handleApiError(error)}`);
      setErrorSnackbarOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmModalOpen(false);
    setTransactionToDelete(null);
  };

  // Ödeme yöntemi değiştirme işlemi başarılı olduğunda
  const handlePaymentMethodChangeSuccess = async () => {
    // İşlem başarılı olduğunda yapılacak işlemler
    setIsPaymentMethodChangeModalOpen(false);
    setSelectedTransaction(null); // Başarılı olduktan sonra seçili transaction'ı temizle

    // Sayfayı yenilemek yerine, sadece işlemleri tekrar yükle (SQL)
    if (!companyId || !accountId) return;

    const numericAccountId = parseInt(accountId, 10);
    const isNumericAccountId = !isNaN(numericAccountId) && numericAccountId.toString() === accountId;
    if (!isNumericAccountId) return;

    setLoading(true);
    setError(null);
    try {
      // SQL'den yeniden yükle (silme akışıyla aynı desen)
      const transactionsResponse = await accountingApi.get(ACCOUNTING_API.TRANSACTIONS_BY_ACCOUNT(numericAccountId));
      const transactionsResult = transactionsResponse.data;
      const sqlTransactions = transactionsResult.data || transactionsResult;

      const formattedTransactions: Transaction[] = sqlTransactions.map((data: any) => {
        const isCollection = data.transactionType === TRANSACTION_TYPES.COLLECTION;

        return {
          id: data.id.toString(),
          accountId: data.accountId ? data.accountId.toString() : accountId,
          accountName: decodeMojibake(data.accountName || accountName),
          transactionType: data.transactionType || '',
          operationType: data.operationType || '',
          type: data.transactionType || 'DEBIT',
          amount: parseFloat(data.amount) || 0,
          debitAmount: parseFloat(data.debitAmount) || 0,
          creditAmount: parseFloat(data.creditAmount) || 0,
          debitBalance: parseFloat(data.debitBalance) || 0,
          description: decodeMojibake(data.description || ''),
          documentNo: data.referenceId || data.documentNo || '',
          referenceId: data.referenceId || '',
          referenceType: data.referenceType || '',
          invoiceNumber: data.invoiceNumber || '',
          date: data.date || data.createdAt || '',
          createdAt: data.createdAt || '',
          currency: data.currency || 'TRY',
          exchangeRate: parseFloat(data.exchangeRate) || 1,
          isIncome: isCollection ? false : (data.creditAmount > 0),
          paymentMethod: data.paymentMethod || '',
          previousBalance: data.previousBalance !== undefined ? parseFloat(data.previousBalance) : undefined,
          nextBalance: data.nextBalance !== undefined ? parseFloat(data.nextBalance) : undefined,
          cashboxId: data.cashboxId,
          cashboxName: data.cashboxName,
          bankAccountId: data.bankAccountId,
          bankAccountName: data.bankAccountName
        };
      });

      // İşlemleri tarihe göre sırala (en yeni en üstte)
      formattedTransactions.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(a.createdAt);
        const dateB = b.date ? new Date(b.date) : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      setTransactions(formattedTransactions);
    } catch (error) {
      console.error('İşlemler yeniden yüklenirken hata (SQL):', error);
      setError('İşlemler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollableContent>
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <IconButton
            onClick={handleGoBack}
            color="primary"
            sx={{ mr: 1, display: { xs: 'flex', md: 'none' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {tt('Geri', 'Back')}
          </Button>
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold" sx={{ ml: { xs: 0, md: 2 } }}>
            {tt('Cari Hareket Detayları', 'Current Transaction Details')} - {accountName}
          </Typography>
        </Box>

        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<AssessmentIcon />}
            onClick={handleOpenLogsModal}
          >
            {tt('Loglar', 'Logs')}
          </Button>
          <GuideButton
            guideKey="accounts.transactions"
            color="secondary"
            sx={{ 
              border: 1, 
              borderColor: 'secondary.main',
              '&:hover': {
                bgcolor: 'secondary.light',
                borderColor: 'secondary.dark'
              }
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={loadingExport || dateAndTypeFilteredTransactions.length === 0}
          >
            {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}
          </Button>
        </Box>

        {/* Mobil Butonlar */}
        <Grid container spacing={1} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? "primary" : "inherit"}
            >
              {showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreler', 'Filters')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="info"
              startIcon={<AssessmentIcon />}
              onClick={handleOpenLogsModal}
            >
              {tt('Loglar', 'Logs')}
            </Button>
          </Grid>
          <Grid item>
            <GuideButton
              guideKey="accounts.transactions"
              color="secondary"
              sx={{ 
                border: 1, 
                borderColor: 'secondary.main',
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  bgcolor: 'secondary.light',
                  borderColor: 'secondary.dark'
                }
              }}
            />
          </Grid>
          <Grid item>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
              disabled={loadingExport || dateAndTypeFilteredTransactions.length === 0}
            >
              {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : 'Excel'}
            </Button>
          </Grid>
        </Grid>
      </HeaderContainer>

      {/* Filtreler */}
      {showFilters && (
        <Box sx={{ mb: 3, display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <DatePicker
              label={tt('Başlangıç Tarihi', 'Start Date')}
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue);
                setPage(0);
              }}
              format="dd.MM.yyyy"
              slotProps={{ 
                textField: { 
                  size: 'small',
                  fullWidth: true
                } 
              }}
            />
            <DatePicker
              label={tt('Bitiş Tarihi', 'End Date')}
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue);
                setPage(0);
              }}
              format="dd.MM.yyyy"
              slotProps={{ 
                textField: { 
                  size: 'small',
                  fullWidth: true
                } 
              }}
            />
          </LocalizationProvider>
          
          <FormControl size="small" fullWidth>
            <InputLabel id="transaction-type-filter-label-mobile">{tt('İşlem Türü', 'Transaction Type')}</InputLabel>
            <Select
              labelId="transaction-type-filter-label-mobile"
              value={selectedTransactionTypeFilter}
              label={tt('İşlem Türü', 'Transaction Type')}
              onChange={(e) => {
                setSelectedTransactionTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>{tt('Tümü', 'All')}</em>
              </MenuItem>
              {Object.entries(TRANSACTION_TYPE_NAMES).map(([type]) => (
                <MenuItem key={type} value={type}>{getTransactionTypeName(type)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" fullWidth>
            <InputLabel id="branch-filter-label-mobile">{tt('Şube', 'Branch')}</InputLabel>
            <Select
              labelId="branch-filter-label-mobile"
              value={selectedBranchFilter}
              label={tt('Şube', 'Branch')}
              disabled={!!lockedBranchId}
              onChange={(e) => {
                setSelectedBranchFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>{tt('Tüm Şubeler', 'All Branches')}</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleClearDateFilter}
            disabled={!startDate && !endDate && !selectedTransactionTypeFilter && !selectedBranchFilter}
            size="small"
          >
            {tt('Temizle', 'Clear')}
          </Button>
        </Box>
      )}

      {/* Desktop Filtreler */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
          <DatePicker
            label={tt('Başlangıç Tarihi', 'Start Date')}
            value={startDate}
            onChange={(newValue) => {
              setStartDate(newValue);
              setPage(0);
            }}
            format="dd.MM.yyyy"
            slotProps={{ 
              textField: { 
                size: 'small', 
                sx: { width: 150 } 
              } 
            }}
          />
          <DatePicker
            label={tt('Bitiş Tarihi', 'End Date')}
            value={endDate}
            onChange={(newValue) => {
              setEndDate(newValue);
              setPage(0);
            }}
            format="dd.MM.yyyy"
            slotProps={{ 
              textField: { 
                size: 'small',
                sx: { width: 150 } 
              } 
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="transaction-type-filter-label">{tt('İşlem Türü', 'Transaction Type')}</InputLabel>
            <Select
              labelId="transaction-type-filter-label"
              value={selectedTransactionTypeFilter}
              label={tt('İşlem Türü', 'Transaction Type')}
              onChange={(e) => {
                setSelectedTransactionTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>{tt('Tümü', 'All')}</em>
              </MenuItem>
              {Object.entries(TRANSACTION_TYPE_NAMES).map(([type]) => (
                <MenuItem key={type} value={type}>{getTransactionTypeName(type)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="branch-filter-label">{tt('Şube', 'Branch')}</InputLabel>
            <Select
              labelId="branch-filter-label"
              value={selectedBranchFilter}
              label={tt('Şube', 'Branch')}
              disabled={!!lockedBranchId}
              onChange={(e) => {
                setSelectedBranchFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>{tt('Tüm Şubeler', 'All Branches')}</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleClearDateFilter}
            disabled={!startDate && !endDate && !selectedTransactionTypeFilter && !selectedBranchFilter}
            size="small"
          >
            {tt('Temizle', 'Clear')}
          </Button>
          
          {(startDate || endDate || selectedTransactionTypeFilter || selectedBranchFilter) && (
            <Chip 
              icon={<FilterAltIcon />} 
              label={`${dateAndTypeFilteredTransactions.length} ${tt('sonuç', 'results')}`}
              color="primary"
              variant="outlined"
            />
          )}
        </LocalizationProvider>
      </Box>

      {/* Üst Bilgi Alanı */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Chip 
            icon={<PaymentIcon />} 
            label={`${tt('Toplam Alacak', 'Total Credit')}: ${formatCurrency(totalCredit)}`} 
            color="success" 
            variant="outlined" 
          />
          <Chip 
            icon={<PaymentIcon />} 
            label={`${tt('Toplam Borç', 'Total Debit')}: ${formatCurrency(debitBalance)}`} 
            color="error" 
            variant="outlined" 
          />
          <Chip 
            icon={<AccountBalanceWalletIcon />} 
            label={`${tt('Bakiye', 'Balance')}: ${formatCurrency(totalCredit - debitBalance)}`} 
            color={(totalCredit - debitBalance) >= 0 ? 'primary' : 'error'} 
            variant="outlined" 
            sx={{ fontWeight: 'bold' }} 
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {/* Desktop: Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab label={t('current.report.tabs.allTransactions')} />
              <Tab label={t('current.report.tabs.receivables')} />
              <Tab label={t('current.report.tabs.payables')} />
              <Tab label={t('current.report.tabs.collections')} />
            </Tabs>
          </Box>

          {/* Mobile: Dropdown */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('current.report.viewSelect')}</InputLabel>
              <Select
                value={activeTab}
                label={t('current.report.viewSelect')}
                onChange={(e) => {
                  setActiveTab(Number(e.target.value));
                  setPage(0);
                }}
              >
                <MenuItem value={0}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ReceiptIcon fontSize="small" />
                    {t('current.report.tabs.allTransactions')}
                  </Box>
                </MenuItem>
                <MenuItem value={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ArrowDownwardIcon fontSize="small" color="success" />
                    {t('current.report.tabs.receivables')}
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ArrowUpwardIcon fontSize="small" color="error" />
                    {t('current.report.tabs.payables')}
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PaymentIcon fontSize="small" color="success" />
                    {t('current.report.tabs.collections')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {dateAndTypeFilteredTransactions.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            }}>
              <Typography variant="h6" component="h2" gutterBottom color="primary.main" fontWeight="bold">
                {(startDate || endDate || selectedTransactionTypeFilter)
                  ? t('current.report.noRecords.filtered')
                  : activeTab === 0 
                    ? t('current.report.noRecords.all')
                    : activeTab === 1 
                      ? t('current.report.noRecords.receivables') 
                      : activeTab === 2
                        ? t('current.report.noRecords.payables')
                        : t('current.report.noRecords.collections')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Desktop: AccountingDataTable */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <AccountingDataTable<Transaction>
                  columns={transactionColumns}
                  rows={paginatedTransactions}
                  getRowId={(row) => row.id}
                  loading={loading}
                  pagination={false}
                  onRowClick={handleOpenDetailModal}
                  getRowSx={getTransactionRowSx}
                  emptyMessage={tt('İşlem kaydı bulunamadı.', 'No transaction records found.')}
                  emptyIcon={<ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                  ariaLabel={tt('cari işlemler tablosu', 'current transactions table')}
                  columnResizeStorageKey="account-transactions"
                />
              </Box>

              {/* Mobile: Kart Görünümü */}
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, p: 2 }}>
                {paginatedTransactions.map((transaction) => (
                  <Grid item xs={12} key={transaction.id}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: transaction.transactionType === TRANSACTION_TYPES.COLLECTION
                          ? 'success.dark'
                          : transaction.isIncome ? 'success.main' : 'error.main',
                        bgcolor: transaction.transactionType === TRANSACTION_TYPES.COLLECTION
                          ? 'success.100'
                          : transaction.isIncome ? 'success.50' : 'error.50',
                        '&:hover': {
                          boxShadow: 4,
                          borderColor: transaction.transactionType === TRANSACTION_TYPES.COLLECTION
                            ? 'success.dark'
                            : transaction.isIncome ? 'success.dark' : 'error.dark'
                        },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOpenDetailModal(transaction)}
                    >
                      {/* Başlık: İşlem Türü ve Menü */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getTransactionTypeIcon(transaction)}
                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary" noWrap>
                            {getTransactionTypeName(transaction.transactionType || transaction.type, transaction.operationType)}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(event) => handleOpenMenu(event, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Divider sx={{ mb: 1 }} />

                      {/* Tarih */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{tt('Tarih', 'Date')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {transaction.date ? formatDate(transaction.date, transaction) : formatDate(transaction.createdAt, transaction)}
                        </Typography>
                      </Box>

                      {/* Ödeme Yöntemi */}
                      {transaction.paymentMethod && (
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{tt('Ödeme', 'Payment')}:</Typography>
                          <Chip
                            label={getPaymentMethodName(transaction.paymentMethod)}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        </Box>
                      )}

                      {/* Açıklama */}
                      {transaction.description && (
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">Açıklama:</Typography>
                          <Typography variant="caption" fontWeight="medium" noWrap>
                            {transaction.description}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* Borç */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{tt('Borç', 'Debit')}:</Typography>
                        <Typography 
                          variant="caption" 
                          fontWeight="medium" 
                          color="error.main"
                        >
                          {transaction.debitAmount ? formatCurrency(transaction.debitAmount, transaction.currency || 'TRY') : '-'}
                        </Typography>
                      </Box>

                      {/* Alacak */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{tt('Alacak', 'Credit')}:</Typography>
                        <Typography 
                          variant="caption" 
                          fontWeight="medium" 
                          color="success.main"
                        >
                          {transaction.creditAmount ? formatCurrency(transaction.creditAmount, transaction.currency || 'TRY') : '-'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              <TablePagination
                component="div"
                count={isSqlAccount ? totalTransactionsCount : dateAndTypeFilteredTransactions.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={isMobile ? "" : tt('Sayfa başına:', 'Rows per page:')}
                labelDisplayedRows={({ from, to, count }) => isTr ? `${from}-${to} / ${count}` : `${from}-${to} of ${count}`}
                sx={DATATABLE_STYLES.pagination}
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Sayfa altına ek boşluk */}
      <Box sx={{ height: '300px' }} />

      {/* İşlem Detay Modalı - Tediye Fişi / Tahsilat Makbuzu */}
      <PaymentReceiptModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        transaction={selectedTransactionForDetail}
        companyId={companyId}
      />

      {/* İşlem Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: '200px' }
        }}
      >
        {/* ✅ YENİ: Fatura işlemleri için Görüntüle ve Kaydı Aç butonları */}
        {(selectedTransaction?.transactionType === 'SALES_INVOICE' || 
          selectedTransaction?.transactionType === 'PURCHASE_INVOICE') && 
          selectedTransaction?.referenceId && (
          <>
            <MenuItem onClick={handleViewInvoice} disabled={loadingInvoice}>
              <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
              {loadingInvoice ? t('current.report.loading') : t('current.report.view')}
            </MenuItem>
            {/* Kaydı Aç admin fatura sayfasına gider; manager (embedded) modda gizli */}
            {!lockedBranchId && (
            <MenuItem onClick={handleEditInvoice}>
              <EditIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
              {t('current.report.openRecord')}
            </MenuItem>
            )}
            <Divider />
          </>
        )}
        {/* İşlem Ayrıntıları */}
        <MenuItem onClick={handleOpenTransactionDetails}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
          {t('current.report.details')}
        </MenuItem>
        <Divider />
        {/* ❌ GİZLENDİ: İşlem hareketini değiştir butonu şu an kullanılmayacak */}
        {/* {selectedTransaction && selectedTransaction.transactionType === TRANSACTION_TYPES.COLLECTION && (
          <MenuItem onClick={handleChangePaymentMethod}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            İşlem hareketini değiştir
          </MenuItem>
        )} */}
        <MenuItem onClick={handleDeleteTransaction}>
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          <Typography color="error.main">{t('current.report.delete')}</Typography>
        </MenuItem>
      </Menu>

      {/* Ödeme Yöntemi Değiştirme Modalı */}
      <TransactionPaymentMethodChangeModal
        open={isPaymentMethodChangeModalOpen}
        onClose={() => {
          setIsPaymentMethodChangeModalOpen(false);
          // Modalı kapattığımızda seçili transaction'ı temizlemeyelim, 
          // çünkü kullanıcı tekrar açabilir
        }}
        transaction={selectedTransaction}
        companyId={companyId}
        onSuccess={handlePaymentMethodChangeSuccess}
      />

      {/* Silme Onay Modalı */}
      <Dialog
        open={isDeleteConfirmModalOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          // ✅ Çek/Senet hareketi kontrolü - çeke bağlı hareketler buradan silinemez,
          // Çek/Senet Yönetimi'nden çek silinince tüm hareketler birlikte silinir.
          const isCheckNoteTransaction =
            (transactionToDelete?.referenceType || '').startsWith('CHECK_NOTE') ||
            ['CHECK_RECEIVED', 'NOTE_RECEIVED', 'CHECK_GIVEN', 'NOTE_GIVEN',
             'CHECK_RETURNED', 'NOTE_RETURNED', 'CHECK_BOUNCED', 'NOTE_BOUNCED',
             'CHECK_ENDORSED', 'NOTE_ENDORSED'].includes(transactionToDelete?.transactionType || '');

          // Entegrasyon işlemi kontrolü - faturaya bağlı işlemler
          const isIntegrationTransaction = !isCheckNoteTransaction && (
            transactionToDelete?.transactionType === 'QUICK_SALE' || 
            transactionToDelete?.transactionType === 'SALES_INVOICE' ||
            transactionToDelete?.transactionType === 'PURCHASE_INVOICE' ||
            // Faturaya bağlı tahsilat hareketleri (hızlı satıştan gelen)
            (transactionToDelete?.transactionType === 'COLLECTION' && 
             (transactionToDelete?.referenceId || /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(transactionToDelete?.description || ''))) ||
            // Açıklamada fatura numarası pattern'i varsa (SF-XXXXXX veya AF-XXXXXX)
            /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(transactionToDelete?.description || ''));
          
          // Fatura numarasını açıklamadan çıkar
          const invoiceNumberMatch = transactionToDelete?.description?.match(/[SA]F-\d{6}-\d{4}/);
          const extractedInvoiceNumber = invoiceNumberMatch ? invoiceNumberMatch[0] : null;

          return (
            <>
              <DialogTitle
                sx={{
                  bgcolor: (isIntegrationTransaction || isCheckNoteTransaction) ? 'warning.main' : 'error.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <WarningIcon />
                <Typography variant="h6">
                  {isCheckNoteTransaction ? 'Çek/Senet Hareketi' : isIntegrationTransaction ? 'Entegrasyon Hareketi' : 'İşlem Silme Onayı'}
                </Typography>
              </DialogTitle>

              <DialogContent sx={{ mt: 2 }}>
                {/* Çek/Senet hareketleri için özel uyarı */}
                {isCheckNoteTransaction && (
                  <>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        🚫 Bu hareket bir çek/senede bağlıdır ve buradan silinemez!
                      </Typography>
                    </Alert>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Bu hareket bir çek/senede bağlı olduğu için silinemez. Lütfen{' '}
                        <strong>Çek/Senet Yönetimi</strong> bölümünden ilgili çeki/senedi siliniz;
                        çek/senet silindiğinde bağlı tüm hareketler birlikte silinir.
                      </Typography>
                      {transactionToDelete?.invoiceNumber && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          📄 Belge Numarası: {transactionToDelete.invoiceNumber}
                        </Typography>
                      )}
                    </Alert>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              İşlem Türü:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeName(transactionToDelete.transactionType || transactionToDelete.type, transactionToDelete.operationType)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Tutar:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Açıklama:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </>
                )}

                {/* Entegrasyon işlemleri için özel uyarı */}
                {!isCheckNoteTransaction && (isIntegrationTransaction ? (
                  <>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        🚫 Bu bir entegrasyon hareketidir ve buradan silinemez!
                      </Typography>
                    </Alert>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Bu işlem bir fatura ile ilişkili olduğundan, doğrudan bu ekrandan silinemez. 
                        Bu işlemi silmek istiyorsanız lütfen <strong>Fatura Yönetimi</strong> modülünden 
                        ilgili faturayı bulup siliniz.
                      </Typography>
                      {extractedInvoiceNumber && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          📄 Fatura Numarası: {extractedInvoiceNumber}
                        </Typography>
                      )}
                    </Alert>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              İşlem Türü:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeName(transactionToDelete.transactionType || transactionToDelete.type, transactionToDelete.operationType)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Tutar:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Açıklama:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <DialogContentText sx={{ mb: 2 }}>
                      Bu işlemi silmek istediğinizden emin misiniz?
                    </DialogContentText>

                    {transactionToDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              İşlem Türü:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {getTransactionTypeName(transactionToDelete.transactionType || transactionToDelete.type, transactionToDelete.operationType)}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Tutar:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              {formatCurrency(transactionToDelete.amount, transactionToDelete.currency || 'TRY')}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Açıklama:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.description || '-'}
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              {tt('Tarih', 'Date')}:
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight="medium">
                              {transactionToDelete.date 
                                ? formatDate(transactionToDelete.date, transactionToDelete) 
                                : formatDate(transactionToDelete.createdAt, transactionToDelete)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        ⚠️ <strong>Bu işlem geri alınamaz!</strong> İşlem silindiğinde, tüm bilgileri log tablosuna kaydedilecektir.
                      </Typography>
                    </Alert>
                  </>
                ))}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                  onClick={handleCancelDelete} 
                  variant="outlined"
                  color="inherit"
                >
                  {(isIntegrationTransaction || isCheckNoteTransaction) ? 'Kapat' : 'İptal'}
                </Button>
                
                {/* Çek/Senet hareketleri için Çek/Senet Yönetimi butonu (manager modda gizli: admin rotası) */}
                {isCheckNoteTransaction ? (
                  !lockedBranchId && (
                  <Button
                    onClick={() => {
                      handleCancelDelete();
                      navigate('/cek-senet');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                  >
                    Çek/Senet Yönetimine Git
                  </Button>
                  )
                ) : isIntegrationTransaction ? (
                  /* Entegrasyon işlemleri için Fatura Yönetimi butonu (manager modda gizli: admin rotası) */
                  !lockedBranchId && (
                  <Button 
                    onClick={() => {
                      handleCancelDelete();
                      navigate('/fatura/satis');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                  >
                    Fatura Yönetimine Git
                  </Button>
                  )
                ) : (
                  <Button 
                    onClick={handleConfirmDelete} 
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    autoFocus
                  >
                    Sil
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Başarı Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccessSnackbarOpen(false)} 
          severity="success" 
          variant="filled"
          icon={<CheckCircleIcon />}
          sx={{ 
            width: '100%',
            fontSize: '1rem'
          }}
        >
          <Typography variant="body1" fontWeight="medium">
            {successMessage}
          </Typography>
        </Alert>
      </Snackbar>

      {/* Hata Snackbar */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={8000}
        onClose={() => setErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setErrorSnackbarOpen(false)} 
          severity="error" 
          variant="filled"
          icon={<ErrorIcon />}
          sx={{ 
            width: '100%',
            fontSize: '1rem'
          }}
        >
          <Typography variant="body1" fontWeight="medium">
            {errorMessage}
          </Typography>
        </Alert>
      </Snackbar>

      {/* Loglar Modalı */}
      <TransactionLogsModal
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
        companyId={companyId}
      />

      {/* ✅ YENİ: Fatura Görüntüleme Modalı */}
      {selectedInvoiceData && (
        <InvoiceDisplayModal
          open={isInvoiceDisplayModalOpen}
          onClose={handleCloseInvoiceDisplayModal}
          invoice={selectedInvoiceData}
          companyId={companyId}
        />
      )}

      {/* İşlem Ayrıntıları Modalı */}
      <Dialog
        open={isTransactionDetailsModalOpen}
        onClose={handleCloseTransactionDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: 'info.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon />
            <Typography variant="h6">
              {t('current.report.transactionDetailsTitle')}
            </Typography>
          </Box>
          <GuideButton
            guideKey="accounts.transactionDetail"
            color="inherit"
            size="small"
            sx={{
              border: '1px solid rgba(255,255,255,0.8)',
              width: 34,
              height: 34
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {transactionForDetails && (
            <Box>
              <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  {/* İşlem ID */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      İşlem ID:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {transactionForDetails.id || '-'}
                    </Typography>
                  </Grid>

                  {/* Cari Hesap ID */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cari Hesap ID:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {transactionForDetails.accountId || '-'}
                    </Typography>
                  </Grid>

                  {/* Cari Hesap Adı */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cari Hesap Adı:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {transactionForDetails.accountName || '-'}
                    </Typography>
                  </Grid>

                  {/* İşlem Türü */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      İşlem Türü (transactionType):
                    </Typography>
                    <Chip 
                      label={getTransactionTypeName(transactionForDetails.transactionType, transactionForDetails.operationType)}
                      size="small"
                      color="primary"
                    />
                  </Grid>

                  {/* Operasyon Türü */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Operasyon Türü (operationType):
                    </Typography>
                    <Chip 
                      label={transactionForDetails.operationType || '-'}
                      size="small"
                      color="secondary"
                    />
                  </Grid>

                  {/* Tutar */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tutar:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {formatCurrency(transactionForDetails.amount, transactionForDetails.currency)}
                    </Typography>
                  </Grid>

                  {/* Borç */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Borç (debitAmount):
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {formatCurrency(transactionForDetails.debitAmount || 0, transactionForDetails.currency)}
                    </Typography>
                  </Grid>

                  {/* Alacak */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Alacak (creditAmount):
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(transactionForDetails.creditAmount || 0, transactionForDetails.currency)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Ödeme Yöntemi */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ödeme Yöntemi:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {transactionForDetails.paymentMethod 
                        ? getPaymentMethodName(transactionForDetails.paymentMethod)
                        : '-'}
                    </Typography>
                  </Grid>

                  {/* Para Birimi */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Para Birimi:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {transactionForDetails.currency || 'TRY'}
                    </Typography>
                  </Grid>

                  {/* Kasa Bilgileri (varsa) */}
                  {(transactionForDetails.cashboxId || transactionForDetails.cashboxName) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Kasa ID:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {transactionForDetails.cashboxId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Kasa Adı:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {transactionForDetails.cashboxName || '-'}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {/* Banka Hesabı Bilgileri (varsa) */}
                  {(transactionForDetails.bankAccountId || transactionForDetails.bankAccountName) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Banka Hesabı ID:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {transactionForDetails.bankAccountId || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Banka Hesabı Adı:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {transactionForDetails.bankAccountName || '-'}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Referans Bilgileri */}
                  {transactionForDetails.referenceId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Referans ID:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {transactionForDetails.referenceId}
                      </Typography>
                    </Grid>
                  )}

                  {transactionForDetails.referenceType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Referans Türü:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {transactionForDetails.referenceType}
                      </Typography>
                    </Grid>
                  )}

                  {/* Belge No */}
                  {transactionForDetails.documentNo && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Belge No:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {transactionForDetails.documentNo}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Açıklama */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Açıklama:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {transactionForDetails.description || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Tarihler */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      İşlem Tarihi (date):
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {transactionForDetails.date ? formatDate(transactionForDetails.date) : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Oluşturulma Tarihi (createdAt):
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {transactionForDetails.createdAt ? formatDate(transactionForDetails.createdAt) : '-'}
                    </Typography>
                  </Grid>

                  {/* Oluşturan */}
                  {transactionForDetails.createdBy && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Oluşturan:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {transactionForDetails.createdBy}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Bakiye Bilgileri */}
                  {transactionForDetails.previousBalance !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Önceki Bakiye:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(transactionForDetails.previousBalance, transactionForDetails.currency)}
                      </Typography>
                    </Grid>
                  )}

                  {transactionForDetails.nextBalance !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sonraki Bakiye:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(transactionForDetails.nextBalance, transactionForDetails.currency)}
                      </Typography>
                    </Grid>
                  )}

                  {/* İşlem Yönü */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      İşlem Yönü:
                    </Typography>
                    <Chip 
                      label={transactionForDetails.isIncome ? 'Gelir' : 'Gider'}
                      size="small"
                      color={transactionForDetails.isIncome ? 'success' : 'error'}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseTransactionDetails} 
            variant="contained"
            color="primary"
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </ScrollableContent>
  );
};

export default AccountTransactions; 